import React, { useState } from 'react';
import {
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  Avatar,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Collapse,
  Divider,
} from '@mui/material';
import {
  Send as SendIcon,
  Delete as DeleteIcon,
  Reply as ReplyIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
} from '@mui/icons-material';
import { GraphScope } from '../../../types/graph.types';
import { useGraphAnnotations } from '../../../hooks/useGraphAnnotations';

interface GraphAnnotationPanelProps {
  nodeId?: string;
  edgeId?: string;
  scope: GraphScope;
}

/**
 * GraphAnnotationPanel
 *
 * Displays annotations for a selected graph node or edge.
 * Supports creating, replying to, resolving, and deleting annotations.
 */
const GraphAnnotationPanel: React.FC<GraphAnnotationPanelProps> = ({
  nodeId,
  edgeId,
  scope,
}) => {
  const {
    annotations,
    loading,
    error,
    createAnnotation,
    deleteAnnotation,
  } = useGraphAnnotations({ nodeId, edgeId, scope });

  const [newContent, setNewContent] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [expandedThreads, setExpandedThreads] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!newContent.trim()) return;
    setSubmitting(true);
    try {
      await createAnnotation(newContent.trim());
      setNewContent('');
    } catch {
      // Error handled by hook
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async (parentId: string) => {
    if (!replyContent.trim()) return;
    setSubmitting(true);
    try {
      await createAnnotation(replyContent.trim(), parentId);
      setReplyContent('');
      setReplyTo(null);
    } catch {
      // Error handled by hook
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (annotationId: string) => {
    try {
      await deleteAnnotation(annotationId);
    } catch {
      // Error handled by hook
    }
  };

  const toggleThread = (annotationId: string) => {
    setExpandedThreads(prev => {
      const next = new Set(prev);
      if (next.has(annotationId)) {
        next.delete(annotationId);
      } else {
        next.add(annotationId);
      }
      return next;
    });
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (!nodeId && !edgeId) {
    return (
      <div style={{ padding: 16, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Select a node or edge to view annotations
        </Typography>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Error */}
      {error && (
        <Alert severity="error" style={{ margin: 8 }} onClose={() => {}}>
          {error}
        </Alert>
      )}

      {/* Create annotation */}
      <div style={{ padding: 16, borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Add an annotation..."
          multiline
          maxRows={4}
          value={newContent}
          onChange={(e) => setNewContent(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSubmit();
            }
          }}
          disabled={submitting}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
          <Button
            size="small"
            variant="contained"
            endIcon={submitting ? <CircularProgress size={16} /> : <SendIcon />}
            onClick={handleSubmit}
            disabled={!newContent.trim() || submitting}
          >
            Add
          </Button>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
          <CircularProgress size={24} />
        </div>
      )}

      {/* Annotations list */}
      {!loading && annotations.length === 0 && (
        <div style={{ padding: 24, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No annotations yet. Be the first to add one.
          </Typography>
        </div>
      )}

      <List style={{ flexGrow: 1, overflow: 'auto', padding: 0 }}>
        {annotations.map((annotation) => (
          <React.Fragment key={annotation.id}>
            <ListItem
              alignItems="flex-start"
              style={{
                flexDirection: 'column',
                paddingLeft: 16,
                paddingRight: 16,
                paddingTop: 12,
                paddingBottom: 12,
              }}
            >
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', width: '100%', marginBottom: 4 }}>
                <Avatar style={{ width: 24, height: 24, fontSize: '0.75rem', marginRight: 8 }}>
                  {(annotation.userName || 'U').charAt(0).toUpperCase()}
                </Avatar>
                <Typography variant="caption" fontWeight={600} style={{ marginRight: 8 }}>
                  {annotation.userName || 'Unknown'}
                </Typography>
                <Typography variant="caption" color="text.secondary" style={{ flexGrow: 1 }}>
                  {formatDate(annotation.createdAt)}
                </Typography>
              </div>

              {/* Content */}
              <Typography variant="body2" style={{ width: '100%', paddingLeft: 32, whiteSpace: 'pre-wrap' }}>
                {annotation.content}
              </Typography>

              {/* Actions */}
              <div style={{ display: 'flex', alignItems: 'center', paddingLeft: 24, marginTop: 4 }}>
                <Tooltip title="Reply">
                  <IconButton
                    size="small"
                    onClick={() => setReplyTo(replyTo === annotation.id ? null : annotation.id)}
                  >
                    <ReplyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Delete">
                  <IconButton size="small" onClick={() => handleDelete(annotation.id)}>
                    <DeleteIcon fontSize="small" color="error" />
                  </IconButton>
                </Tooltip>

                {/* Thread expand button */}
                {annotation.replies && annotation.replies.length > 0 && (
                  <Button
                    size="small"
                    onClick={() => toggleThread(annotation.id)}
                    endIcon={expandedThreads.has(annotation.id) ? <CollapseIcon /> : <ExpandIcon />}
                    style={{ marginLeft: 'auto', fontSize: '0.7rem' }}
                  >
                    {annotation.replies.length} {annotation.replies.length === 1 ? 'reply' : 'replies'}
                  </Button>
                )}
              </div>

              {/* Reply input */}
              <Collapse in={replyTo === annotation.id} style={{ width: '100%', paddingLeft: 32, marginTop: 8 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Write a reply..."
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleReply(annotation.id);
                      }
                    }}
                    disabled={submitting}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => handleReply(annotation.id)}
                    disabled={!replyContent.trim() || submitting}
                  >
                    Reply
                  </Button>
                </div>
              </Collapse>

              {/* Thread replies */}
              {annotation.replies && annotation.replies.length > 0 && (
                <Collapse in={expandedThreads.has(annotation.id)} style={{ width: '100%', paddingLeft: 32, marginTop: 8 }}>
                  {annotation.replies.map((reply) => (
                    <div
                      key={reply.id}
                      style={{
                        paddingLeft: 16,
                        paddingTop: 8,
                        paddingBottom: 8,
                        borderLeft: '2px solid rgba(0,0,0,0.12)',
                        marginBottom: 4,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                        <Avatar style={{ width: 20, height: 20, fontSize: '0.65rem', marginRight: 4 }}>
                          {(reply.userName || 'U').charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography variant="caption" fontWeight={600} style={{ marginRight: 8 }}>
                          {reply.userName || 'Unknown'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(reply.createdAt)}
                        </Typography>
                      </div>
                      <Typography variant="body2" style={{ paddingLeft: 24, fontSize: '0.85rem' }}>
                        {reply.content}
                      </Typography>
                    </div>
                  ))}
                </Collapse>
              )}
            </ListItem>
            <Divider component="li" />
          </React.Fragment>
        ))}
      </List>
    </div>
  );
};

export default GraphAnnotationPanel;
